import { endpoints } from '@/react-query/endpoints';

import { addMessage } from '../../app/snackbar/SnackbarActions';
import { makeAPIRequest, makeAPIRequestV2 } from '../../utils/api';
import { sendException } from '../../utils/sentry';
import AnnotatorConstants from '../annotator/annotator.constants';

import {
  DISCONNECTED,
  ERROR,
  FETCH_ORGANIZATION_FAILURE,
  FETCH_ORGANIZATION_SUCCESS,
  FETCH_PRACTICE_FAILURE,
  FETCH_PRACTICE_SUCCESS,
  GET_USER_FAILURE,
  GET_USER_SUCCESS,
  LOGOUT_FAILURE,
  LOGOUT_SUCCESS,
  UPDATE_PRACTICE_RBL_THRESHOLD,
  UPDATE_PRIMARY_PRACTICE,
  UPDATE_USER_SUCCESS,
  UPDATE_VIDEA_BEACON_CONNECTION_STATUS,
} from './login.constants';

export function updateUser(user: any) {
  return async (dispatch: any) => {
    const {
      data: { updatedAt },
    } = await makeAPIRequest(
      `/classes/_User/${user.objectId}`,
      { data: user },
      'PUT',
    );
    return dispatch({
      type: UPDATE_USER_SUCCESS,
      user: {
        ...user,
        updatedAt,
      },
    });
  };
}

export function getUser(jwtToken: any) {
  return async (dispatch: any) => {
    const getUserResponse = await makeAPIRequestV2(
      '/user',
      {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
        },
      },
      'GET',
    );

    if (getUserResponse) {
      dispatch({
        type: GET_USER_SUCCESS,
        response: { user: getUserResponse?.data },
      });
    } else {
      dispatch(
        addMessage(
          'User config error - please contact Videa for support.',
          'error',
        ),
      );
      dispatch({
        type: GET_USER_FAILURE,
      });
    }

    return getUserResponse;
  };
}

export function logout() {
  return async (dispatch: any) => {
    try {
      await makeAPIRequest('/logout', {}, 'POST');
      localStorage.removeItem(
        AnnotatorConstants.IMAGE_ROTATIONS_LOCAL_STORAGE_KEY,
      );
      return dispatch({ type: LOGOUT_SUCCESS });
    } catch (e) {
      console.error(e);
      sendException(e, { tags: [['action', 'LOGOUT_FAILURE']] });
      return dispatch({ type: LOGOUT_FAILURE });
    }
  };
}

export function updateVideaBeaconConnectionStatus(
  newStatus: any,
  errorMessage: any,
) {
  return {
    type: UPDATE_VIDEA_BEACON_CONNECTION_STATUS,
    data: {
      newStatus,
      errorMessage,
    },
  };
}

export function processWebSocketCloseEvent(event: any) {
  return (dispatch: any) => {
    switch (event.code) {
      case 1000:
        // A clean closure of the WebSocket (perhaps the user logged out?)
        dispatch(
          updateVideaBeaconConnectionStatus(
            DISCONNECTED,
            'VideaBeacon Disconnected...',
          ),
        );
        break;
      case 1006:
        dispatch(
          updateVideaBeaconConnectionStatus(
            ERROR,
            'Connection dropped with a WS Code 1006: Likely due to VideaBeacon not running. Please ensure VideaBeacon is running.',
          ),
        );
        break;
      case 1009:
        dispatch(
          updateVideaBeaconConnectionStatus(
            ERROR,
            'Connection dropped with a WS Code 1009: WS message was too large.',
          ),
        );
        break;
      case 1015:
        dispatch(
          updateVideaBeaconConnectionStatus(
            ERROR,
            'Connection dropped with a WS Code 1015: Handshake error.',
          ),
        );
        break;
      default:
        dispatch(
          updateVideaBeaconConnectionStatus(
            ERROR,
            `WS Code ${event.code}: error reason (could be undefined): ${event.reason}`,
          ),
        );
        break;
    }
  };
}

export function getOrganization(jwtToken: any, organizationId: any) {
  return async (dispatch: any) => {
    let organizationResponse;
    const errorMessage = `Could not find details for organization id=${organizationId}`;
    try {
      organizationResponse = await makeAPIRequestV2(
        `/organizations?filter=${organizationId}`,
        {
          headers: { Authorization: `Bearer ${jwtToken}` },
        },
        'GET',
      );
    } catch (error) {
      console.error(errorMessage, error);
      dispatch({
        type: FETCH_ORGANIZATION_FAILURE,
      });
    }

    if (organizationResponse) {
      dispatch({
        type: FETCH_ORGANIZATION_SUCCESS,
        response: { organization: organizationResponse?.data },
      });
    } else {
      console.error(errorMessage);
      dispatch({
        type: FETCH_ORGANIZATION_FAILURE,
      });
    }
    return organizationResponse;
  };
}

/* 
Fetch additional practice metadata given a JWT token context during login.
Log API request failures but otherwise swallow exceptions so login doesn't fail.
 */
export function getPractice(
  organizationId: any,
  practiceId: any,
  isDirectorView = false,
  accessToken?: string,
) {
  return async (dispatch: any) => {
    let practiceResponse;
    if (!accessToken || !organizationId || !practiceId) {
      return;
    }
    try {
      practiceResponse = await makeAPIRequestV2(
        `/${endpoints.organizations}/${organizationId}/${endpoints.practices}/${practiceId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            details: 'practice',  // Request practice details only
          }
        },
        'GET',
      );
    } catch (error) {
      console.error(
        `Could not find details for practice id=${practiceId} belonging to organization id=${organizationId}`,
        error,
      );
      dispatch({
        type: FETCH_PRACTICE_FAILURE,
      });
    }

    if (practiceResponse) {
      dispatch({
        type: FETCH_PRACTICE_SUCCESS,
        response: { practice: practiceResponse?.data?.practice },
      });
      /** This is for temporarily setting the primary practice for a director taking the 'View in Videa AI' link from VideaInsights */
      if (isDirectorView && practiceResponse?.data?.practice) {
        dispatch({
          type: UPDATE_PRIMARY_PRACTICE,
          payload: {
            practiceId,
            practiceName: practiceResponse?.data?.practice?.name,
          },
        });
      }
    } else {
      console.error(
        `Could not find details for practice id=${practiceId} belonging to organization id=${organizationId}`,
      );
      dispatch({
        type: FETCH_PRACTICE_FAILURE,
      });
    }
    return practiceResponse;
  };
}

export const updatePracticeRBLThreshold = (threshold: string) => {
  return (dispatch: any) =>
    dispatch({ type: UPDATE_PRACTICE_RBL_THRESHOLD, payload: threshold });
};
